/**
 * German presentation of the engine's generated English names. The engine's names are data
 * ("12th Infantry Division", "Kingdom of Varnia", "The Varnian Marches", "Zlatovina Heights");
 * the proper names inside them stay as they are, the English grammar around them is re-set in
 * German: "12. Infanteriedivision", "Königreich Varnia", "Die Varnischen Marken",
 * "Zlatovina-Höhen". German also needs what English does not: gender, articles and adjective
 * endings ("der Varnischen Republik", "varnische Truppen", "im Dornmark-Wald").
 *
 * Every parser falls back to the English text unchanged, so a name form the engine adds later
 * still prints — just without the German polish. Pure; unit-tested in test/i18n.test.ts.
 */

import { REAL_ADJECTIVES, REAL_NATIONS, REAL_REGIONS, REAL_THEATRES, REAL_TOWNS } from './places.de';

export type Gender = 'm' | 'f' | 'n' | 'pl';
export type Case = 'nom' | 'acc' | 'dat';

const ARTICLE: Record<Gender, Record<Case, string>> = {
  m: { nom: 'der', acc: 'den', dat: 'dem' },
  f: { nom: 'die', acc: 'die', dat: 'der' },
  n: { nom: 'das', acc: 'das', dat: 'dem' },
  pl: { nom: 'die', acc: 'die', dat: 'den' },
};

/** Adjective ending after the definite article (weak declension). */
const WEAK: Record<Gender, Record<Case, string>> = {
  m: { nom: 'e', acc: 'en', dat: 'en' },
  f: { nom: 'e', acc: 'e', dat: 'en' },
  n: { nom: 'e', acc: 'e', dat: 'en' },
  pl: { nom: 'en', acc: 'en', dat: 'en' },
};

/** Adjective ending without an article (strong declension). */
const STRONG: Record<Gender, Record<Case, string>> = {
  m: { nom: 'er', acc: 'en', dat: 'em' },
  f: { nom: 'e', acc: 'e', dat: 'er' },
  n: { nom: 'es', acc: 'es', dat: 'em' },
  pl: { nom: 'e', acc: 'e', dat: 'en' },
};

export const article = (g: Gender, c: Case): string => ARTICLE[g][c];

const cap = (s: string): string => s.charAt(0).toUpperCase() + s.slice(1);

// ---- national adjectives -------------------------------------------------------------------------

/**
 * The German national adjective behind an English one from the engine's `adjectiveOf`:
 *  - Germanic/Nordic lands take "-er" ("Ostmarker", "Halvheimer") — in German these are the
 *    indeclinable place adjectives of "Berliner Zeitung", so they stay exactly as they are;
 *  - "Varnian" (Varnia), "Kaldavan" (Kaldava), "Skaudenian" (Skaudene), "Szolanyi" (Szolany)
 *    and the "-ish" unions ("Brennish") become "-isch" stems: varnisch, kaldavisch …
 */
export interface NationalAdjective {
  /** "varnisch" — or the indeclinable "Ostmarker". */
  stem: string;
  indeclinable: boolean;
  /** The land's name when it can be recovered ("Varnia", "Ostmark"), else null. */
  land: string | null;
}

export function nationalAdjective(en: string): NationalAdjective {
  const w = en.trim();
  const real = REAL_ADJECTIVES[w];
  if (real) return { stem: real.stem, indeclinable: false, land: real.land };
  if (/(mark|land|heim|au|gau)er$/.test(w)) return { stem: w, indeclinable: true, land: w.slice(0, -2) };
  if (/[a-z]ish$/.test(w))
    return { stem: `${w.slice(0, -3).toLowerCase()}isch`, indeclinable: false, land: null };
  let land: string | null = null;
  let stem = w;
  if (/(any|eny|sag)i$/.test(w)) {
    land = w.slice(0, -1);
    stem = land;
  } else if (/an$/.test(w)) {
    // Lands ending in "a" take "n" ("Varnia" → "Varnian"); anything else took "an". The rare
    // Baltic "-ene" land ("Skaudene" → "Skaudenian") reads back as "Skaudenia": same stem.
    land = w.charAt(w.length - 2) === 'a' ? w.slice(0, -1) : w.slice(0, -2);
    stem = land.replace(/i?a$/, '');
  }
  return { stem: `${stem.toLowerCase()}isch`, indeclinable: false, land };
}

/**
 * The adjective with an ending, in prose case: "varnische", "varnischen" — or "Ostmarker".
 * `ending` is the declension suffix the noun phrase needs ("e", "en", "er", "es", "em").
 */
export function adj(en: string, ending: string): string {
  const a = nationalAdjective(en);
  return a.indeclinable ? a.stem : a.stem + ending;
}

/** Capitalised, as part of a proper name: "Varnische", "Ostmarker". */
export const adjCap = (en: string, ending: string): string => cap(adj(en, ending));

/** First element of a compound adjective ("varnisch-kaldavisch"): always the -isch form. */
function compoundStem(en: string): string {
  const a = nationalAdjective(en);
  if (!a.indeclinable) return a.stem;
  const land = a.land ?? a.stem;
  const umlaut = land.replace(/mark$/, 'märk').replace(/land$/, 'länd');
  return `${umlaut.toLowerCase()}isch`;
}

/** Short German label of a nation for lists and chips: its land ("Varnia"), else the full name. */
export function nationShort(n: { name: string; adjective: string }): string {
  const real = REAL_NATIONS[n.name];
  if (real) return real.short;
  return nationalAdjective(n.adjective).land ?? nation(n).bare;
}

// ---- nations -------------------------------------------------------------------------------------

/** A German nation name: an optional (capitalised) adjective, a head noun with gender. */
export interface NationName {
  /** Nominative without article: "Varnische Republik", "Königreich Varnia". */
  bare: string;
  gender: Gender;
  /** False for names we could not parse: they are printed as they are, without article. */
  articled: boolean;
  /** With the definite article in a case: "der Varnischen Republik", "dem Königreich Varnia". */
  the(c: Case): string;
}

type Parts = { adj: string | null; noun: string; gender: Gender };

const OF_FORMS: [RegExp, string, Gender][] = [
  [/^Grand Duchy of (.+)$/, 'Großherzogtum', 'n'],
  [/^Kingdom of (.+)$/, 'Königreich', 'n'],
  [/^Republic of (.+)$/, 'Republik', 'f'],
  [/^Principality of (.+)$/, 'Fürstentum', 'n'],
  [/^Free State of (.+)$/, 'Freistaat', 'm'],
  [/^Duchy of (.+)$/, 'Herzogtum', 'n'],
  [/^Tsardom of (.+)$/, 'Zarentum', 'n'],
  [/^Regency of (.+)$/, 'Regentschaft', 'f'],
];

const ADJ_FORMS: [RegExp, string, Gender][] = [
  [/^(.+) Union$/, 'Union', 'f'],
  [/^(.+) Commonwealth$/, 'Staatenbund', 'm'],
  [/^(.+) Confederation$/, 'Konföderation', 'f'],
  [/^(.+) Federation$/, 'Föderation', 'f'],
];

function nationParts(name: string, adjective: string): Parts | null {
  for (const [re, noun, gender] of OF_FORMS) {
    const m = re.exec(name);
    if (m) return { adj: null, noun: `${noun} ${m[1]}`, gender };
  }
  const united = /^United Provinces of (.+)$/.exec(name);
  if (united) return { adj: 'Vereinigt', noun: `Provinzen von ${united[1]}`, gender: 'pl' };
  const rep = /^(.+) Republic$/.exec(name);
  // "Varnian Republic" → Varnische Republik; "Ostmark Republic" (a land used attributively) → Republik Ostmark.
  if (rep)
    return rep[1] === adjective
      ? { adj: adjective, noun: 'Republik', gender: 'f' }
      : { adj: null, noun: `Republik ${rep[1]}`, gender: 'f' };
  for (const [re, noun, gender] of ADJ_FORMS) if (re.test(name)) return { adj: adjective, noun, gender };
  return null;
}

/** Inflect a nation's own adjective ("Vereinigt" is German already; others are English). */
function nameAdj(parts: Parts, ending: string): string {
  if (!parts.adj) return '';
  if (parts.adj === 'Vereinigt') return `Vereinigt${ending} `;
  return `${adjCap(parts.adj, ending)} `;
}

export function nation(n: { name: string; adjective: string }): NationName {
  const real = REAL_NATIONS[n.name];
  if (real) {
    const { gender, adjective, noun } = real;
    return {
      bare: real.bare,
      gender,
      articled: real.articled,
      the: (c) =>
        !real.articled
          ? real.bare
          : adjective && noun
            ? `${ARTICLE[gender][c]} ${cap(adjective + WEAK[gender][c])} ${noun}`
            : `${ARTICLE[gender][c]} ${real.bare}`,
    };
  }
  const parts = nationParts(n.name, n.adjective);
  if (!parts) return { bare: n.name, gender: 'n', articled: false, the: () => n.name };
  const { gender } = parts;
  return {
    bare: `${nameAdj(parts, STRONG[gender].nom)}${parts.noun}`,
    gender,
    articled: true,
    the: (c) => `${ARTICLE[gender][c]} ${nameAdj(parts, WEAK[gender][c])}${parts.noun}`,
  };
}

/**
 * A nation known only by its English name (the server's chat line "Victory: Brennish Union"):
 * the adjective is the word before Union/Commonwealth/…/Republic — unless that word is a
 * Germanic land used attributively ("Ostmark Republic"). Returns the German bare name.
 */
export function nationFromName(name: string): string {
  const real = REAL_NATIONS[name];
  if (real) return real.bare;
  const m = /^(\S+) (Union|Commonwealth|Confederation|Federation|Republic)$/.exec(name);
  const adjective = m && !(m[2] === 'Republic' && /(mark|land|heim|au)$/.test(m[1])) ? m[1] : '';
  return nation({ name, adjective }).bare;
}

/** "hat" or "haben" for a nation as the subject ("Die Vereinigten Provinzen … haben"). */
export const has = (n: NationName): string => (n.gender === 'pl' ? 'haben' : 'hat');

// ---- theatres ------------------------------------------------------------------------------------

/** A theatre of war: title ("Die Varnischen Marken") and "in"-phrase ("in den Varnischen Marken"). */
export interface TheatreName {
  title: string;
  /** Where the war is fought, with its preposition: "an der Pforte von Dornmark". */
  inside: string;
}

export function theatre(en: string): TheatreName {
  const real = REAL_THEATRES[en];
  if (real) return real;
  const make = (gender: Gender, prep: string, adjective: string | null, noun: string): TheatreName => {
    const weakNom = adjective ? `${adjCap(adjective, WEAK[gender].nom)} ` : '';
    const weakDat = adjective ? `${adjCap(adjective, WEAK[gender].dat)} ` : '';
    const art = ARTICLE[gender];
    const dat = prep === 'im' ? `im ${weakDat}${noun}` : `${prep} ${art.dat} ${weakDat}${noun}`;
    return { title: `${cap(art.nom)} ${weakNom}${noun}`, inside: dat };
  };
  const m = /^The (\S+)-(\S+) Borderlands$/.exec(en);
  if (m) {
    // Both halves capitalised, as in a proper name: "Das Varnisch-Kaldavische Grenzland".
    const pair = `${cap(compoundStem(m[1]))}-${cap(compoundStem(m[2]))}`;
    return { title: `Das ${pair}e Grenzland`, inside: `im ${pair}en Grenzland` };
  }
  // [pattern, gender, preposition, adjective form (the group is a national adjective), noun]
  const FORMS: [RegExp, Gender, string, boolean, (x: string) => string][] = [
    [/^The (\S+) Marches$/, 'pl', 'in', true, () => 'Marken'],
    [/^The (\S+) Frontier$/, 'f', 'an', true, () => 'Grenze'],
    [/^The (\S+) Highlands$/, 'n', 'im', true, () => 'Hochland'],
    [/^The (\S+) Lowlands$/, 'n', 'im', true, () => 'Tiefland'],
    [/^The Plains of (.+)$/, 'f', 'in', false, (x) => `Ebene von ${x}`],
    [/^The (.+) Gap$/, 'f', 'an', false, (x) => `Pforte von ${x}`],
    [/^The (.+) Salient$/, 'm', 'im', false, (x) => `Bogen von ${x}`],
    [/^The (.+) Corridor$/, 'm', 'im', false, (x) => `Korridor von ${x}`],
  ];
  for (const [re, gender, prep, isAdj, noun] of FORMS) {
    const f = re.exec(en);
    if (f) return make(gender, prep, isAdj ? f[1] : null, noun(f[1]));
  }
  return { title: en, inside: `im Kriegsgebiet „${en}“` };
}

// ---- regions -------------------------------------------------------------------------------------

/** A province: its German name and the "in"-phrase ("in der Zlatovina", "im Dornmark-Wald"). */
export interface RegionName {
  name: string;
  gender: Gender | null;
  inside: string;
  /** Dative with its article, for "über …" / "aus …": "der Zlatovina", "den Zlatovina-Höhen". */
  dat: string;
}

/** Germanic-looking bases are fused with Ober-/Nieder- ("Oberdornmark"), others hyphenated. */
const GERMANIC_BASE = /(mark|land|gau|au|wald|dal|heim|lagen|bygd)$/i;

/** Gender of a bare province name from its ending, as German treats such names. */
function regionGender(name: string): Gender | null {
  if (/(mark|au)$/i.test(name) && !/gau$/i.test(name)) return 'f';
  if (/(gau|wald)$/i.test(name)) return 'm';
  if (/(land|dal)$/i.test(name)) return 'n';
  // Like the Bukowina and the Herzegowina.
  if (/ina$/i.test(name)) return 'f';
  return null;
}

function regionName(name: string, gender: Gender | null): RegionName {
  const dat = gender ? `${ARTICLE[gender].dat} ${name}` : name;
  const inside = !gender ? `in ${name}` : gender === 'm' || gender === 'n' ? `im ${name}` : `in ${dat}`;
  return { name, gender, inside, dat };
}

export function region(en: string): RegionName {
  const real = REAL_REGIONS[en];
  const declined = real?.[2];
  if (real && declined) {
    // A name with a declined adjective: "dem Oberen Don" → "im Oberen Don"; "der Oberen Wolga".
    const [name, gender] = real;
    const dat = declined;
    const inside = gender === 'm' || gender === 'n' ? `im ${dat.slice(4)}` : `in ${dat}`;
    return { name, gender, inside, dat };
  }
  if (real) return regionName(real[0], real[1]);
  let m = /^(.+) (Heights|Woods|Fens)$/.exec(en);
  if (m) {
    const [noun, gender]: [string, Gender] =
      m[2] === 'Heights' ? ['Höhen', 'pl'] : m[2] === 'Woods' ? ['Wald', 'm'] : ['Moor', 'n'];
    return regionName(`${m[1]}-${noun}`, gender);
  }
  m = /^(Upper|Lower) (.+)$/.exec(en);
  if (m) {
    const prefix = m[1] === 'Upper' ? 'Ober' : 'Nieder';
    const name = GERMANIC_BASE.test(m[2]) ? `${prefix}${m[2].toLowerCase()}` : `${prefix}-${m[2]}`;
    return regionName(name, regionGender(m[2]));
  }
  return regionName(en, regionGender(en));
}

// ---- unit designations ---------------------------------------------------------------------------

/**
 * "12th Infantry Division", optionally with the nation ("11th Skaudavan Motorized Division"),
 * and the variants other generators use ("Armoured", "Panzer", "Reconnaissance").
 */
const DIVISION =
  /^(\d+)(?:st|nd|rd|th) (?:(\S+) )?(Infantry|Motori[sz]ed|Armou?red|Panzer|Artillery|Cavalry|Reconnaissance) Division$/;
const ARMY_HQ = /^([IVXLCDM]+) (?:(\S+) )?Army HQ$/;

/** English arm → [full German noun, staff abbreviation]. */
function arm(en: string): [string, string] {
  if (en === 'Infantry') return ['Infanteriedivision', 'Inf.Div.'];
  if (/^Motori/.test(en)) return ['Infanteriedivision (mot.)', 'Inf.Div. (mot.)'];
  if (en === 'Artillery') return ['Artilleriedivision', 'Art.Div.'];
  if (en === 'Cavalry' || en === 'Reconnaissance') return ['Kavalleriedivision', 'Kav.Div.'];
  return ['Panzerdivision', 'Pz.Div.'];
}

/**
 * "12th Infantry Division" → "12. Infanteriedivision"; with a nation, "11. skaudavische
 * Infanteriedivision (mot.)"; "II Army HQ" → "Armeeoberkommando II".
 */
export function designation(en: string): string {
  const d = DIVISION.exec(en);
  if (d) return `${d[1]}. ${d[2] ? `${adj(d[2], 'e')} ` : ''}${arm(d[3])[0]}`;
  const h = ARMY_HQ.exec(en);
  return h ? `${h[2] ? `${adjCap(h[2], 'es')} ` : ''}Armeeoberkommando ${h[1]}` : en;
}

/** "12th Infantry Division" → "12. Inf.Div."; "II Army HQ" → "AOK II" (the staff drops the nation). */
export function designationShort(en: string): string {
  const d = DIVISION.exec(en);
  if (d) return `${d[1]}. ${arm(d[3])[1]}`;
  const h = ARMY_HQ.exec(en);
  return h ? `AOK ${h[1]}` : en;
}

// ---- towns ---------------------------------------------------------------------------------------

/** A town's German name: the established exonym of a real town ("Warsaw" → "Warschau"), else as is. */
export const town = (en: string): string => REAL_TOWNS[en] ?? en;

/**
 * The town adjective of German newspaper titles: "Dornheim" → "Dornheimer", "Kesslingen" →
 * "Kesslinger", "Zlatica" → "Zlaticaer" (like "Jenaer"). Null for names that do not take it.
 */
export function townAdjective(name: string): string | null {
  if (!/[a-zäöüß]$/i.test(name)) return null;
  if (/[^aeiouäöü]en$/.test(name)) return `${name.slice(0, -2)}er`;
  if (/e$/.test(name)) return `${name}r`;
  return `${name}er`;
}

// ---- wind ----------------------------------------------------------------------------------------

const WIND: Record<string, string> = {
  E: 'Ost',
  NE: 'Nordost',
  N: 'Nord',
  NW: 'Nordwest',
  W: 'West',
  SW: 'Südwest',
  S: 'Süd',
  SE: 'Südost',
};

/** Compass code → the word German weather reports use: "W" → "West" ("Wind aus West"). */
export const windWord = (code: string): string => WIND[code] ?? code;
